import React, { useState } from 'react';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';

const SHORT_TEXT =
  'Quantitative risk estimates use a FAIR-aligned methodology and are for informational purposes only. ' +
  'This analysis is not a certified FAIR assessment.';

const LONG_TEXT = `Quantitative financial risk estimates presented in this platform use a methodology \
aligned with the Factor Analysis of Information Risk (FAIR) framework, a recognized industry standard \
for cyber risk quantification. These estimates are generated by Axarix's proprietary risk engine, which \
incorporates FAIR-aligned concepts including Threat Event Frequency (TEF), Vulnerability Probability \
(VULN), Loss Event Frequency (LEF), and Loss Magnitude (LM) components.

Important Limitations:
• This is not a certified or formally licensed FAIR® assessment. FAIR® is a registered trademark of \
The Open Group. Axarix is not an Open Group member or FAIR-certified organization.
• All estimates are probabilistic models based on available data inputs and calibration parameters. \
Actual losses may differ materially from any estimate.
• Monte Carlo simulations and scenario analyses are mathematical models, not guarantees of future \
outcomes.
• These estimates should not be used as the sole basis for financial, insurance, or regulatory decisions.
• Results depend on the accuracy and completeness of assessment data provided by users.

For certified FAIR assessments or formal risk quantification engagements, consult a qualified \
risk quantification professional or Open Group FAIR-certified practitioner.`;

export default function FairMethodologyDisclaimer({ variant = 'inline' }) {
  const [expanded, setExpanded] = useState(false);

  if (variant === 'tooltip') {
    return (
      <span
        title={LONG_TEXT}
        className="inline-flex items-center gap-1 text-xs text-gray-400 cursor-help"
      >
        <Info className="w-3 h-3" />
        FAIR-aligned methodology
      </span>
    );
  }

  return (
    <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
      <div className="flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
        <div className="flex-1">
          <p>{SHORT_TEXT}</p>
          {expanded && (
            <div className="mt-2 space-y-2 whitespace-pre-line text-gray-400">{LONG_TEXT}</div>
          )}
        </div>
        <button
          onClick={() => setExpanded(v => !v)}
          className="shrink-0 text-gray-400 hover:text-gray-600"
          aria-label={expanded ? 'Collapse disclaimer' : 'Expand disclaimer'}
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}
